import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { MessageSquare, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Human labels for feature ids returned by GET /api/admin/advisor/chip-insights.
// Kept aligned with `frontend/src/components/chat/featureChips.js`.
const FEATURE_LABEL_KEYS = {
  scenario_analyzer: 'aiAdvisor.chips.scenarioAnalyzer',
  deal_analyzer: 'aiAdvisor.chips.dealAnalyzer',
  deal_comparison: 'aiAdvisor.chips.dealComparison',
  portfolio_doctor: 'aiAdvisor.chips.portfolioDoctor',
  portfolio_dashboard: 'aiAdvisor.chips.portfolioDashboard',
  portfolio_goals: 'aiAdvisor.chips.portfolioGoals',
};

const WINDOW_OPTIONS = [7, 30, 90];

/**
 * Admin Chip Insights — shows the platform-wide ranking of which RendPleX
 * feature chips users tap from inside the AI Advisor. Hidden entirely for
 * Free users (the backend endpoint returns 403 and we render nothing).
 */
export default function AdvisorChipInsights() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [gated, setGated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/admin/advisor/chip-insights`, {
        params: { days },
        withCredentials: true,
      })
      .then((res) => {
        if (!cancelled) {
          setData(res.data);
          setGated(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err?.response?.status === 403) setGated(true);
          setData(null);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [days]);

  if (gated) return null; // Free users don't see this card at all

  const total = data?.total_chip_clicks || 0;
  const chips = data?.chips_by_feature || [];
  const topCount = chips[0]?.count || 1;

  return (
    <div
      className="mb-8 bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-card"
      data-testid="advisor-chip-insights-section"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#12355B] to-[#45C04A] rounded-xl flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">
              {t('adminChipInsights.title')}
            </h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('adminChipInsights.subtitle')}
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800/70 rounded-full p-1"
          role="tablist"
          aria-label={t('adminChipInsights.windowLabel')}
        >
          {WINDOW_OPTIONS.map((d) => (
            <button
              key={d}
              data-testid={`advisor-chip-window-${d}`}
              onClick={() => setDays(d)}
              className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                days === d
                  ? 'bg-white dark:bg-slate-900 text-[#12355B] dark:text-white shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
              }`}
              aria-pressed={days === d}
            >
              {t('adminChipInsights.lastNDays', { n: d })}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 py-4">
          {t('common.loading')}
        </p>
      ) : total === 0 ? (
        <p
          className="text-sm text-slate-500 dark:text-slate-400 py-4"
          data-testid="advisor-chip-insights-empty"
        >
          {t('adminChipInsights.empty')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                {t('adminChipInsights.totalClicks')}
              </p>
              <p className="text-2xl font-bold text-[#12355B] dark:text-white">
                {total}
              </p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                {t('adminChipInsights.uniqueUsers')}
              </p>
              <p className="text-2xl font-bold text-[#45C04A]">
                {data?.unique_users || 0}
              </p>
            </div>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-3.5 col-span-2 md:col-span-1">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                {t('adminChipInsights.topRoute')}
              </p>
              <p className="text-sm font-semibold text-[#12355B] dark:text-white truncate">
                {chips[0]
                  ? t(FEATURE_LABEL_KEYS[chips[0].feature_id] || 'adminChipInsights.unknownFeature')
                  : '—'}
              </p>
            </div>
          </div>

          <ol className="space-y-2" data-testid="advisor-chip-insights-ranking">
            {chips.map((row, idx) => {
              const pct = Math.round((row.count / topCount) * 100);
              const label = t(
                FEATURE_LABEL_KEYS[row.feature_id] || 'adminChipInsights.unknownFeature'
              );
              return (
                <li
                  key={row.feature_id}
                  data-testid={`advisor-chip-rank-${row.feature_id}`}
                  className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/40 px-3.5 py-2.5"
                >
                  <div
                    className="absolute left-0 top-0 bottom-0 bg-[#45C04A]/10 dark:bg-[#45C04A]/15"
                    style={{ width: `${pct}%` }}
                    aria-hidden="true"
                  />
                  <div className="relative flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-[11px] font-bold text-slate-500 dark:text-slate-400">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                        {label}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {t('adminChipInsights.usersCount', {
                          count: row.unique_users,
                        })}
                      </span>
                      <span className="inline-flex items-center gap-1 text-sm font-bold text-[#12355B] dark:text-white">
                        <TrendingUp className="w-3.5 h-3.5 text-[#45C04A]" />
                        {row.count}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </>
      )}
    </div>
  );
}
